import { gql, useQuery } from "@apollo/client";
import { useApplicationContext } from "components/Application";
import { CartRows } from "components/CartRows";
import { CenterSpinner } from "components/CenterSpinner";
import { Footer } from "components/Footer";
import { capitalize } from "lodash";
import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import tw from "twin.macro";

const orderDocumentQuery = gql`
  query OrderDocument($brandId: ID, $isStock: Boolean) {
    config: default {
      id
      promoQty: wholesalePromoQuantity
    }
    accountSession {
      currentUser {
        cartDocument {
          id
          code
          notes
          rows(brandId: $brandId, isStock: $isStock) {
            id
            item {
              id
              code
              color {
                id
                code
                nameEn
                nameIt
                patch
              }
              stocks {
                quantity
                size {
                  id
                }
              }
            }
            referenceItem {
              id
              code
              cover
              nameEn
              nameIt
              shortDescriptionEn
              shortDescriptionIt
              brand {
                id
              }
              season {
                id
              }
              sizeGroup {
                id
                code
                sizes {
                  id
                  code
                  value
                }
              }
              externalComposition {
                id
              }
            }
            sizeQuantities {
              id
              quantity
              size {
                id
                code
              }
            }
          }
          paymentMethod {
            id
            code
            nameEn
            nameIt
          }
        }
      }
    }
  }
`;

export const Cart: React.FC<{}> = () => {
  const { session } = useApplicationContext();
  const { user, currentUser: cUser } = session;
  const {
    cartDocument: { tabs },
  } = cUser;

  let visibleTabs = tabs;
  if (user.role === "SALESPERSON" && user.id !== cUser.id) {
    visibleTabs = [];
    const { userBrands } = cUser;
    tabs.forEach((tab: any) => {
      userBrands.forEach((userBrand: any) => {
        if (userBrand.brand.id === tab.brand.id && userBrand.salesperson.id === user.id) visibleTabs.push(tab);
      });
    });
  }

  const [activeTab, setActiveTab] = useState({
    index: 0,
    brand: visibleTabs[0]?.brand.id,
    stock: visibleTabs[0]?.season.isStock,
    shipping: visibleTabs[0]?.season.shippingWindow,
  });

  const variables = {
    brandId: activeTab.brand,
    isStock: activeTab.stock,
    shipping: activeTab.shipping,
  };
  const { loading, data, refetch } = useQuery(orderDocumentQuery, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const { i18n } = useTranslation();
  const locale = capitalize(i18n.language);

  if (loading) return <CenterSpinner size="large" />;

  const { accountSession, config } = data;
  const { currentUser } = accountSession;
  const { cartDocument } = currentUser;
  const { promoQty } = config;

  return (
    <div tw="flex space-x-2">
      <div tw="flex flex-col space-x-2 w-full">
        <div tw="flex w-full justify-between px-6">
          <div tw="text-gray-500">{i18n.language === "it" ? "CARRELLO" : "CART"}</div>
        </div>

        <div tw="text-center lg:w-screen lg:flex lg:justify-center md:w-full md:grid md:grid-cols-2 md:gap-2">
          {visibleTabs?.map((tab: any, i: any) => {
            const brandName = tab.brand[`name${locale}`].toUpperCase(),
              seasonName = tab.season[`name${locale}`].toUpperCase();
            return (
              <Fragment key={i}>
                <span
                  css={[tw`py-1 px-10 text-gray-500 cursor-pointer`, activeTab.index === i && tw`text-gray-900`]}
                  onClick={() => setActiveTab({ index: i, brand: tab.brand.id, stock: tab.season.isStock, shipping: tab.season.shippingWindow})}
                >
                  {i18n.language === "it" ? "ORDINE" : "ORDER"} {brandName} {seasonName}
                </span>
              </Fragment>
            );
          })}
        </div>

        <CartRows
          promoQty={promoQty}
          document={cartDocument}
          brandId={activeTab.brand}
          stock={activeTab.stock}
          refetch={refetch}
          shipping={activeTab.shipping}
        />

        <Footer />
      </div>
    </div>
  );
};
